// PROJECTION_CHOKEPOINT — VisibleFact, VisibleOp, and the wire-level
// visibility delta carriers (VisibilityAdded / VisibilityRemoved) may
// only be constructed inside this package.
//
// PRIMARY enforcement is the compile-time nominal brand below: the
// read-projection carriers (VisibleFact / VisibleOp / VisibleSegment)
// each carry a phantom `[PROJECTION_BRAND]` field keyed on a
// non-exported `unique symbol`. Because the symbol is never exported, no
// code outside this package can NAME the key — so no external object
// literal, `satisfies`, typed variable, return annotation, or structural
// pass can produce one of these carriers; the value MUST come from a
// factory in this file (each stamps the brand via a local `as` cast).
// This is enforced everywhere by `tsc` (typecheck), not just a grep.
//
// The brand is phantom — `declare const` gives it NO runtime value, so
// the `[PROJECTION_BRAND]` fields never exist at runtime. It is therefore
// transparent to consumers, JSON serialization, and structural equality:
// external code receives these carriers strictly BY VALUE.
//
// The wire-delta carriers (VisibilityAdded / VisibilityRemoved) keep
// their EXPORTED factories — the Campaign DO builds wire frames from them
// (apps/do-campaign/src/sync-handler.ts) — plus their value-free
// signatures as the guarantee.
//
// scripts/check-projection-chokepoint.sh remains as belt-and-suspenders
// for the residual explicit-`as`-cast escape hatch, alongside the
// phantom-Viewer wire-level test (apps/worker/test/phantom-viewer-wire.test.ts).

/**
 * Non-exported nominal brand. `declare const` → a type-level marker with NO
 * runtime value, so the branded interface fields below never exist at runtime
 * (transparent to JSON / `toEqual` / spreads). Factories in this file apply it
 * with a local `as` cast; nothing outside this package can name the key, which
 * is what makes external construction a `tsc` error.
 */
declare const PROJECTION_BRAND: unique symbol;

/**
 * A fact as it appears to a Viewer after projection — provenance, draft
 * status, and any non-revealed metadata are stripped.
 *
 * **`pending` flag** (I.A.5 / #97 / 07-session-experience.md §8). When the
 * underlying fact's folded status is `pending`, the projection sets
 * `pending: true` on the `VisibleFact`. The full status enum is NOT leaked
 * to non-GM Viewers — only this minimal boolean, just enough for the UI
 * to render an "awaiting GM cleanup" indicator. The flag is omitted when
 * the fact is canon, so a vanilla `VisibleFact` (no `pending` key) is
 * still the steady-state shape.
 */
export interface VisibleFact {
  /** Phantom nominal brand — see PROJECTION_BRAND. Not present at runtime. */
  readonly [PROJECTION_BRAND]: 'VisibleFact';
  readonly subject: string;
  readonly predicate: string;
  readonly value: unknown;
  /** Present and `true` iff the folded status is `pending`. Absent otherwise. */
  readonly pending?: true;
}

/**
 * The brand-free structural shape of a projected fact — `VisibleFact` minus the
 * nominal brand. It exists for exactly ONE purpose: the *baseline* ("prior
 * frame") argument to {@link import('./diff.js').diffProjections}. A server
 * broadcaster (the Campaign DO) necessarily REHYDRATES the previous frame from
 * storage — `ws.serializeAttachment` round-trips it through plain JSON — so the
 * baseline cannot carry the phantom brand. `VisibleFact` is assignable to this
 * type (the brand is an extra field), so a freshly-projected baseline works too.
 *
 * This is NOT a construction escape hatch: `diffProjections` only READS these
 * fields and emits deltas through the branded factories. You still cannot MAKE
 * a `VisibleFact` from a `ProjectedFactSnapshot` — the brand is unforgeable
 * outside this package.
 */
export interface ProjectedFactSnapshot {
  readonly subject: string;
  readonly predicate: string;
  readonly value: unknown;
  readonly pending?: true;
}

/**
 * A reveal op as it appears to the GM (for audit / Replay surfaces) — kept
 * here for the same chokepoint reason even though WS scope doesn't yet
 * surface reveals to non-GM viewers.
 */
export interface VisibleOp {
  /** Phantom nominal brand — see PROJECTION_BRAND. Not present at runtime. */
  readonly [PROJECTION_BRAND]: 'VisibleOp';
  readonly subject: string;
  readonly predicate: string;
  readonly value: unknown;
}

/**
 * Internal constructor — used by ./project.ts. Not exported from this
 * package's index. External callers receive VisibleFact / VisibleOp BY
 * VALUE and never call this.
 *
 * `options.pending` (default `false`) sets the `pending: true` flag on the
 * returned record; `false` omits the key entirely (so steady-state canon
 * facts shipping nothing extra over the wire).
 */
export function makeVisibleFact(
  subject: string,
  predicate: string,
  value: unknown,
  options: { pending?: boolean } = {},
): VisibleFact {
  // `as VisibleFact` here is the ONLY place the phantom brand is applied for
  // this carrier — the field has no runtime value (see PROJECTION_BRAND).
  if (options.pending) {
    return Object.freeze({ subject, predicate, value, pending: true as const }) as VisibleFact;
  }
  return Object.freeze({ subject, predicate, value }) as VisibleFact;
}

/**
 * A single revealed valid-time segment of a `(subject, base-predicate)` as it
 * appears to a Viewer after projection (ADR-0005 §"Export" / S8). Unlike
 * `VisibleFact` — which collapses the whole timeline to the one segment
 * covering a query `t` — a `VisibleSegment` carries the segment's own
 * VIEWER-CLAMPED half-open `[from, to)` validity, so the export path can emit
 * the WHOLE revealed timeline for the Viewer, not a cursor snapshot.
 *
 * Phantom-Viewer: this is still constructed ONLY inside `packages/projection`
 * and carries only segments the Viewer was revealed, with bounds already
 * clamped to the facets the Viewer may observe (an un-revealed `to` exports as
 * `undefined` → open-ended). `owner_belief` is the believer's viewer key and is
 * present only for the GM (omniscient) export, never leaked to a non-GM Viewer
 * — a non-GM only ever receives their OWN belief, projected under the base
 * predicate exactly as `VisibleFact` does.
 */
export interface VisibleSegment {
  /** Phantom nominal brand — see PROJECTION_BRAND. Not present at runtime. */
  readonly [PROJECTION_BRAND]: 'VisibleSegment';
  readonly subject: string;
  /** The BASE predicate (segments of one base collapse under one key). */
  readonly predicate: string;
  readonly value: unknown;
  /**
   * The viewer-clamped half-open validity window for THIS segment. `from`
   * inclusive, `to` exclusive (ADR-0005 §4); an absent bound is open in that
   * direction. `null` is fully timeless. An un-revealed `to` was clamped away
   * upstream (reads open-forward — no leak).
   */
  readonly validity: { readonly from?: number; readonly to?: number } | null;
  /** Present and `true` iff the folded status is `pending`. Absent otherwise. */
  readonly pending?: true;
  /**
   * `'belief'` for a belief segment (GM omniscient export only), else `'canon'`.
   */
  readonly owner_kind: 'canon' | 'belief';
  /** Believer's stable viewer key — GM export only; absent for canon. */
  readonly owner_belief?: string;
}

/**
 * Internal constructor for {@link VisibleSegment} — used by ./project.ts only,
 * not exported from the package index. External callers receive
 * `VisibleSegment` BY VALUE and never call this.
 */
export function makeVisibleSegment(args: {
  subject: string;
  predicate: string;
  value: unknown;
  validity: { from?: number; to?: number } | null;
  pending?: boolean;
  owner_kind: 'canon' | 'belief';
  owner_belief?: string;
}): VisibleSegment {
  const base: {
    subject: string;
    predicate: string;
    value: unknown;
    validity: { from?: number; to?: number } | null;
    owner_kind: 'canon' | 'belief';
    pending?: true;
    owner_belief?: string;
  } = {
    subject: args.subject,
    predicate: args.predicate,
    value: args.value,
    validity: args.validity === null ? null : Object.freeze({ ...args.validity }),
    owner_kind: args.owner_kind,
  };
  if (args.pending) base.pending = true;
  if (args.owner_belief !== undefined) base.owner_belief = args.owner_belief;
  // `as VisibleSegment` applies the phantom brand (no runtime field).
  return Object.freeze(base) as VisibleSegment;
}

/**
 * Wire-level visibility-delta envelope payloads. These are the projected
 * "since the last frame, you now see X / you no longer see X" notices
 * the server emits to a non-GM Viewer (III.A.3 / #63).
 *
 * They share the chokepoint with `VisibleFact` so the
 * `apps/do-workspace` server-side projection caller has ONE place to
 * import the constructors from, and so the CI grep treats the whole
 * surface as "must originate inside packages/projection".
 */
export interface VisibilityAdded {
  readonly kind: 'visibility_added';
  readonly subject: string;
  readonly predicate: string;
  readonly value: unknown;
  readonly pending?: true;
  /** Optional debug hint — the reveal op that surfaced this fact. */
  readonly via_reveal_op?: string;
}

/**
 * Critically: `VisibilityRemoved` carries ONLY the fact id. No value, no
 * payload, no metadata that could leak post-removal. The wire-level
 * phantom-Viewer test (#64) asserts this shape directly.
 */
export interface VisibilityRemoved {
  readonly kind: 'visibility_removed';
  readonly subject: string;
  readonly predicate: string;
  /** Optional debug hint — the op that caused the removal. */
  readonly via_op?: string;
}

export function makeVisibilityAdded(
  fact: VisibleFact,
  options: { via_reveal_op?: string } = {},
): VisibilityAdded {
  const base = {
    kind: 'visibility_added' as const,
    subject: fact.subject,
    predicate: fact.predicate,
    value: fact.value,
  };
  const withPending = fact.pending ? { ...base, pending: true as const } : base;
  return Object.freeze(
    options.via_reveal_op ? { ...withPending, via_reveal_op: options.via_reveal_op } : withPending,
  );
}

export function makeVisibilityRemoved(
  subject: string,
  predicate: string,
  options: { via_op?: string } = {},
): VisibilityRemoved {
  // Defensive: this constructor takes NO value parameter, by design. The
  // function signature is the proof that no caller can accidentally pipe
  // a value into a "removed" wire frame.
  const base = { kind: 'visibility_removed' as const, subject, predicate };
  return Object.freeze(options.via_op ? { ...base, via_op: options.via_op } : base);
}
